// src/containers/reportBuilder/useReportActivityData.js
//
// Fetches per-activity detail data (venues, objectives, cross-refs, fast
// facts, evaluation plans, shortlisted/survey comments, chart images) for
// the primary selected activity. A hook rather than something owned inside
// a single component because the result is needed in two places that don't
// share a parent below the page/embed root: the Report tab's preview
// (ReportPreviewSection — all of it) and the Charts tab, rendered inside
// ReportBuilderPanel (just evaluationPlans + chartImages, to seed rating
// panels and persisted chart PNGs). Call it once at the top (admin page or
// embedded wrapper) and thread the relevant slices down to each.

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { reportBuilderGet } from '../../lib/chartApi';

export default function useReportActivityData(selectedActivities) {
  const primarySelectedId = selectedActivities[0]?.id ?? null;

  const [venues, setVenues] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [crossRefs, setCrossRefs] = useState(null);
  const [fastFacts, setFastFacts] = useState(null);
  const [evaluationPlans, setEvaluationPlans] = useState(null);
  // Persisted chart PNGs from sReportCustomisation — keyed by evaluationId.
  const [chartImages, setChartImages] = useState({});
  const [shortlistedComments, setShortlistedComments] = useState({
    all: [],
    byOutcome: {},
  });
  // "Appendix 2: Survey responses" — all respondent comments (not just
  // shortlisted), grouped by evaluation plan then pivoted by outcome.
  const [surveyResponseComments, setSurveyResponseComments] = useState({
    hasComments: false,
    sessions: [],
  });

  // Persist a chart PNG (or removal) to sReportCustomisation when toggled in Charts tab.
  // The server deep-merges chartImages so patching a single key is safe.
  const handleChartImageChange = useCallback(
    (evaluationId, png) => {
      const activityId = primarySelectedId;
      if (!activityId) return;
      if (png) {
        setChartImages((prev) => ({ ...prev, [evaluationId]: png }));
        axios
          .patch(`/project/${activityId}/report-customisation`, {
            chartImages: { [evaluationId]: png },
          })
          .catch(() => {});
      } else {
        setChartImages((prev) => {
          const next = { ...prev };
          delete next[evaluationId];
          return next;
        });
        // For removal we must send the full chartImages object minus the removed key,
        // because the server merge can only add/update keys, not delete them.
        axios
          .get(`/project/${activityId}/report-customisation`)
          .then(({ data }) => {
            const current = { ...(data?.customisation?.chartImages || {}) };
            delete current[evaluationId];
            return axios.patch(`/project/${activityId}/report-customisation`, {
              chartImages: current,
            });
          })
          .catch(() => {});
      }
    },
    [primarySelectedId]
  );

  // Shared by the initial-selection effect below and the visibility-regain
  // refresh, so a colleague's edits to the activity while this panel is
  // unfocused (or just sitting open) are picked up on return rather than
  // silently going stale — the activity isn't locked from editing just
  // because someone has its report open.
  const fetchActivityData = useCallback(
    (primaryId, isCancelled = () => false) => {
      reportBuilderGet('activity-venues', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled()) setVenues(j.ok ? j.venues || null : null);
        })
        .catch(() => {
          if (!isCancelled()) setVenues(null);
        });

      reportBuilderGet('activity-objectives', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled())
            setObjectives(j.ok && j.objectives?.length ? j.objectives : null);
        })
        .catch(() => {
          if (!isCancelled()) setObjectives(null);
        });

      reportBuilderGet('activity-cross-references', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled()) setCrossRefs(j.ok ? j.crossRefs || null : null);
        })
        .catch(() => {
          if (!isCancelled()) setCrossRefs(null);
        });

      reportBuilderGet('activity-fast-facts', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled()) setFastFacts(j.ok ? j : null);
        })
        .catch(() => {
          if (!isCancelled()) setFastFacts(null);
        });

      reportBuilderGet('activity-evaluation-plans', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled())
            setEvaluationPlans(
              j.ok && j.evaluationPlans?.length ? j.evaluationPlans : null
            );
        })
        .catch(() => {
          if (!isCancelled()) setEvaluationPlans(null);
        });

      reportBuilderGet('shortlisted-comments', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled())
            setShortlistedComments(
              j.ok
                ? { all: j.all || [], byOutcome: j.byOutcome || {} }
                : { all: [], byOutcome: {} }
            );
        })
        .catch(() => {
          if (!isCancelled())
            setShortlistedComments({ all: [], byOutcome: {} });
        });

      reportBuilderGet('survey-response-comments', { activityId: primaryId })
        .then((j) => {
          if (!isCancelled())
            setSurveyResponseComments(
              j.ok
                ? { hasComments: !!j.hasComments, sessions: j.sessions || [] }
                : { hasComments: false, sessions: [] }
            );
        })
        .catch(() => {
          if (!isCancelled())
            setSurveyResponseComments({ hasComments: false, sessions: [] });
        });

      axios
        .get(`/project/${primaryId}/report-customisation`)
        .then(({ data }) => {
          if (!isCancelled())
            setChartImages(data?.customisation?.chartImages || {});
        })
        .catch(() => {
          if (!isCancelled()) setChartImages({});
        });
    },
    []
  ); // setters + reportBuilderGet + axios are all referentially stable

  useEffect(() => {
    const primaryId = primarySelectedId;
    if (!primaryId) {
      setVenues(null);
      setObjectives(null);
      setCrossRefs(null);
      setFastFacts(null);
      setEvaluationPlans(null);
      setChartImages({});
      setShortlistedComments({ all: [], byOutcome: {} });
      setSurveyResponseComments({ hasComments: false, sessions: [] });
      return;
    }
    let cancelled = false;

    setVenues(null);
    setObjectives(null);
    setCrossRefs(null);
    setFastFacts(null);
    setEvaluationPlans(null);
    setChartImages({});
    setShortlistedComments({ all: [], byOutcome: {} });
    setSurveyResponseComments({ hasComments: false, sessions: [] });

    fetchActivityData(primaryId, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [primarySelectedId, fetchActivityData]);

  // ── Refetch activity data when tab/panel regains focus ───────────────────
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible' || !primarySelectedId) return;
      fetchActivityData(primarySelectedId);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, [primarySelectedId, fetchActivityData]);

  return {
    venues,
    objectives,
    crossRefs,
    fastFacts,
    evaluationPlans,
    chartImages,
    shortlistedComments,
    surveyResponseComments,
    handleChartImageChange,
  };
}
